import React, { useEffect } from 'react';
import '../css/ProductSelection.scss';
import { useFormikContext } from 'formik';
import { AvailableProducts, MagicianDoseTypes } from 'app/constants/Products';
import protocolNames from 'app/utils/protocolNames';
import { Badge, Card, Button } from 'mui';
import Radio from 'app/components/common/Radio';
import PriceFormatter from 'app/components/customer/steps/Payment/Generic/PriceFormatter';
import { MAGICIAN_DOSE_TYPE_SUBSCRIPTION_COST } from 'app/helpers/productSpecificPriceCalculators/magicianPriceCalculator';
import assetLibrary from 'app/utils/assetLibrary';
import doseTypeNames from 'app/utils/doseTypeNames';
import { getDrugVariantForDoseType, getStrengthVariantForDrugAndDose } from './magicianUtils';

type Props = {
  handleSubmit?: () => void;
  changeProductHandler?: ((kingProductType: string, setFieldValue: any) => void) | null;
  multimonth_plan?: number | null;
  available_doses: Array<string>;
};

const MagicianDoseSelectionForm = ({
  changeProductHandler = null,
  handleSubmit = undefined,
  available_doses,
}: Props) => {
  const { values, setFieldValue } = useFormikContext<{
    dose_type: string;
    drug_variant: string;
    strength_variant: string;
  }>();

  useEffect(() => {
    if (!available_doses.includes('standard')) {
      handleChange('micro');
      return;
    }

    handleChange(values.dose_type);
  }, []);

  const handleChange = (value: string) => {
    setFieldValue('dose_type', value);
    setFieldValue('drug_variant', getDrugVariantForDoseType(values.drug_variant, value));
    setFieldValue('strength_variant', getStrengthVariantForDrugAndDose(values.drug_variant, value));

    if (changeProductHandler) {
      changeProductHandler(value, setFieldValue);
    }
  };

  const doseTypeConfigPrice = (doseType) => {
    return MAGICIAN_DOSE_TYPE_SUBSCRIPTION_COST[doseType][1];
  };

  const dosesConfig = [
    {
      name: MagicianDoseTypes.Standard,
      price: doseTypeConfigPrice(MagicianDoseTypes.Standard),
      description:
        'For significant weight loss and metabolic benefits. Ideal for those with BMI >25 seeking substantial results.',
    },
    {
      name: MagicianDoseTypes.Micro,
      price: doseTypeConfigPrice(MagicianDoseTypes.Micro),
      description: 'Customized lower dose for metabolic benefits and modest weight loss.',
    },
  ];

  const renderRadio = (selectedDoseType, doseType, showDivider) => (
    <>
      {doseType.name === 'standard' && available_doses.includes('standard') && (
        <Badge variant="outline">Recommended</Badge>
      )}
      <Radio
        className="no-borders top-aligned"
        onChange={() => handleChange(doseType.name)}
        checked={selectedDoseType === doseType.name}
        disabled={!available_doses.includes(doseType.name)}
        testId={`dose-type-${doseType.name}`}
        label={
          <div className="-mt-6 lg:-mt-5">
            <div className="flex flex-row justify-between items-end">
              <h5>{doseTypeNames[doseType.name]}</h5>
              <PriceFormatter period={'mo'} from price={doseType.price} />
            </div>
            <p className="mt-2 lg:mt-3 text-sm lg:text-base">{doseType.description}</p>
          </div>
        }
      />
      {doseType.name === 'standard' && !available_doses.includes('standard') && (
        <Badge variant="outline">Not Eligible</Badge>
      )}
      {showDivider && <hr />}
    </>
  );

  const image = assetLibrary[`prod-magician-standard-micro`];

  return (
    <Card>
      <Card.Body>
        <figure className="h-[180px] lg:h-[240px] mb-4">
          <img className="h-full w-auto" src={image} alt="Product" />
        </figure>

        <div className="text-center mt-4">
          <Badge variant={AvailableProducts.Magician} className="mb-2">
            {protocolNames[AvailableProducts.Magician]}
          </Badge>
        </div>

        {dosesConfig.map((doseType, index) =>
          renderRadio(values.dose_type, doseType, index !== dosesConfig.length - 1),
        )}
        {handleSubmit && (
          <>
            <hr />

            <Button onClick={handleSubmit} data-testid="submit-dose-selection">
              Continue
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default MagicianDoseSelectionForm;
